import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

export enum ManageStock {
  MANAGE_STOCK_AT_TEST_LEVEL = "Manage stock at test level",
  MANAGE_STOCK_AT_TEST_GROUP_LEVEL = "Manage stock at test group level",
}
export enum DeductStock {
  DEDUCT_STOCK_AT_SAMPLE_COLLECTION = "Deduct stock at sample collection",
  DEDUCT_STOCK_AT_PERFORM_TEST = "Deduct stock at perform test",
}

type Asset = {
  _id: string;
  assetName?: string;
  measuringType?: string;
  qty?: number;
};

type Row = {
  assetId: string;
  assetName?: string;
  measuringType: string;
  quantity: number | string;
  manageStock: ManageStock | string;
  deductStock: DeductStock | string;
};
export enum MEASURING_TYPES {
  Mass = "Mass",
  Liquid = "Liquid",
  Gas = "Gas",
  Count = "Count",
}

const API_BASE = process.env.REACT_APP_LAB_URL;

type RowErrors = {
  assetId: boolean;
  measuringType: boolean;
  quantity: boolean;
};

export default function AddAsset(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [errors, setErrors] = useState<RowErrors[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testName, setTestName] = useState("");
  const [charges, setCharges] = useState("");
  const [testCode, setTestCode] = useState("");

  const authHeader = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  const firstMeasuringType = Object.values(MEASURING_TYPES)[0];

  useEffect(() => {
    // keep errors in sync with rows length
    setErrors((prev) => {
      const next: RowErrors[] = rows.map(
        (r, i) =>
          prev[i] ?? {
            assetId: false,
            measuringType: false,
            quantity: false,
          }
      );
      return next;
    });
  }, [rows.length]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const assetsRes = await axios.get(`${API_BASE}/assets/getAllAssetsWithSearch`, {
          headers: authHeader,
        });
        const assetsList: Asset[] = assetsRes?.data?.data ?? [];
        if (!mounted) return;
        setAssets(assetsList);

        if (id) {
          try {
            const testRes = await axios.get(
              `${API_BASE}/tests/getOneLabTest/${id}`,
              {
                headers: authHeader,
              }
            );
            const response = testRes.data?.data;
            console.log(response, ";red");
            setTestCode(response?.testCode);
            setTestName(response?.testName);
            setCharges(response?.testCharges);
            const existing: any[] =
              testRes.data?.data?.testItems ?? testRes.data?.testItems ?? [];
            if (Array.isArray(existing) && existing.length) {
              setRows(
                existing.map((t) => ({
                  assetId: t.assetId ?? "",
                  assetName: t.assetName ?? "",
                  measuringType: t.measuringType ?? "",
                  quantity: t.quantity ?? 1,
                  manageStock:
                    t.manageStock ?? ManageStock.MANAGE_STOCK_AT_TEST_LEVEL,
                  deductStock:
                    t.deductStock ??
                    DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION,
                }))
              );
              return;
            }
          } catch (error: any) {
            toast.error("GET test by id failed");
            toast.error(error?.response.data.message || "Failed to load test details");
          }
        }

        setRows([
          {
            assetId: "",
            assetName: "",
            measuringType: "",
            quantity: 1,
            manageStock: ManageStock.MANAGE_STOCK_AT_TEST_LEVEL,
            deductStock: DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION,
          },
        ]);
      } catch (error: any) {
        console.error("load failed", error);
        toast.error(error.message || "Failed to load assets");
        setRows([
          {
            assetId: "",
            assetName: "",
            measuringType: "",
            quantity: 1,
            manageStock: ManageStock.MANAGE_STOCK_AT_TEST_LEVEL,
            deductStock: DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION,
          },
        ]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const emptyRow = (): Row => ({
    assetId: "",
    assetName: "",
    measuringType: "",
    quantity: 1,
    manageStock: ManageStock.MANAGE_STOCK_AT_TEST_LEVEL,
    deductStock: DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION,
  });

  const updateRow = (index: number, patch: Partial<Row>) =>
    setRows((s) => s.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const updateError = (index: number, patch: Partial<RowErrors>) =>
    setErrors((s) =>
      s.map((er, i) => (i === index ? { ...er, ...patch } : er))
    );

  const addRow = () => {
    if (rows.length > assets.length - 1) {
      toast.info("Rows Cannot Exceed More");
      return;
    }
    setRows((s) => [...s, emptyRow()]);
    setErrors((s) => [
      ...s,
      { assetId: false, measuringType: false, quantity: false },
    ]);
  };

  const removeRow = (index: number) => {
    setRows((s) => s.filter((_, i) => i !== index));
    setErrors((s) => s.filter((_, i) => i !== index));
  };

  const validateAll = (): boolean => {
    const nextErrors: RowErrors[] = rows.map((r) => ({
      assetId: !r.assetId,
      measuringType: !r.measuringType,
      quantity: r.quantity === "" || Number.isNaN(Number(r.quantity)),
    }));

    setErrors(nextErrors);

    const anyError = nextErrors.some(
      (er) => er.assetId || er.measuringType || er.quantity
    );
    if (anyError) {
      toast.error("Please fill required fields correctly.");
    }
    return !anyError;
  };

  const handleSave = async () => {
    if (!validateAll()) {
      return;
    }

    const ids = rows.map((r) => r.assetId);
    const hasDuplicate = ids.some((id, idx) => ids.indexOf(id) !== idx);

    if (hasDuplicate) {
      toast.error("Duplicate assets found. Please select unique assets.");
      return;
    }

    const payload = {
      testItems: rows.map((t) => ({
        assetId: t.assetId,
        measuringType: t.measuringType,
        quantity: Number(t.quantity),
        manageStock: t.manageStock,
        deductStock: t.deductStock,
      })),
    };

    const endpoint = `${API_BASE}/tests/updateTestItems/${id}`;

    setSaving(true);
    try {
      const res = await axios.put(endpoint, payload, { headers: authHeader });
      toast.success(res?.data?.message);
      // navigate(-1);
    } catch (e: any) {
      console.error("save error", e);
      toast.error(e?.response?.data?.message ?? "Failed to save test items");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Box flex="1 1 auto" minWidth={0}>
          <Box display="flex" gap={3} flexWrap="wrap">
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Test Name: </span>
              <span style={{ color: "#616161" }}>{testName}</span>
            </Typography>
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Charges: </span>
              <span style={{ color: "#616161" }}>{charges}</span>
            </Typography>
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Test Code: </span>
              <span style={{ color: "#616161" }}>{testCode}</span>
            </Typography>
          </Box>
          {/* <Typography
            variant="h6"
            noWrap
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Test items / tools
          </Typography> */}
        </Box>

        <Box flex="0 0 auto">
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate("/settings/test-tab")}
            sx={{ mr: 1 }}
          >
            Back To TestList
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ boxShadow: "none" }}
      >
        <Table
          size="small"
          aria-label="test items table"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: { xs: 820, sm: "100%" },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f6fb" }}>
              <TableCell sx={{ fontWeight: 500, width: "22%" }}>
                Item name
              </TableCell>
              <TableCell sx={{ fontWeight: 500, width: "18%" }}>
                Measuring Type
              </TableCell>
              <TableCell sx={{ fontWeight: 500, width: "10%" }}>
                Quantity
              </TableCell>
              <TableCell sx={{ fontWeight: 500, width: "20%" }}>
                Manage Stock
              </TableCell>
              <TableCell sx={{ fontWeight: 500, width: "20%" }}>
                Deduct Stock
              </TableCell>
              <TableCell
                sx={{
                  width: "10%",
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Tooltip title="Add Row">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        py: 0,
                        px: 0,
                        p: 0,
                        minWidth: "25px",
                      }}
                      onClick={addRow}
                    >
                      <AddIcon sx={{ fontSize: "22px" }} />
                    </Button>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => {
              const rowErr = errors[idx] ?? {
                assetId: false,
                measuringType: false,
                quantity: false,
              };
              return (
                <TableRow key={idx} sx={{ backgroundColor: "#ffffff" }}>
                  <TableCell sx={{ borderBottom: "none", p: 0.4 }}>
                    <Autocomplete
                      size="small"
                      options={assets}
                      getOptionLabel={(opt: Asset) => opt.assetName ?? opt._id}
                      value={assets.find((a) => a._id === row.assetId) ?? null}
                      onChange={(_e, value: Asset | null) => {
                        if (!value) {
                          updateRow(idx, {
                            assetId: "",
                            assetName: "",
                            measuringType: row.measuringType,
                            quantity: row.quantity ?? 1,
                          });
                          updateError(idx, { assetId: true }); // mark asset empty
                          return;
                        }
                        const alreadySelected = rows.some(
                          (r, i) => i !== idx && r.assetId === value._id
                        );

                        if (alreadySelected) {
                          toast.warning(
                            "This asset is already selected in another row!"
                          );
                        }
                        // set measuringType from asset if provided otherwise keep current or default to firstMeasuringType
                        updateRow(idx, {
                          assetId: value._id,
                          assetName: value.assetName ?? "",
                          measuringType:
                            value.measuringType ??
                            row.measuringType ??
                            firstMeasuringType,
                          quantity: 1,
                        });
                        // clear errors for this row
                        updateError(idx, {
                          assetId: false,
                          measuringType: false,
                          quantity: false,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Search asset..."
                          className="custom-input"
                          inputProps={{
                            ...params.inputProps,
                          }}
                          fullWidth
                          error={rowErr.assetId}
                        />
                      )}
                      isOptionEqualToValue={(option, val) =>
                        option._id === val._id
                      }
                      sx={{ width: "100%" }}
                    />
                  </TableCell>

                  <TableCell sx={{ borderBottom: "none", p: 0.4 }}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={rowErr.measuringType}
                    >
                      <Select
                        value={row.measuringType || ""}
                        displayEmpty
                        onChange={(e) => {
                          updateRow(idx, {
                            measuringType: e.target.value as MEASURING_TYPES,
                          });
                          updateError(idx, { measuringType: false });
                        }}
                        className="custom-input"
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiSelect-select": { py: "4.5px" },
                        }}
                        renderValue={(selected) =>
                          selected ? selected : "Select Type"
                        }
                      >
                        <MenuItem value="">
                          <em>Select unit...</em>
                        </MenuItem>

                        {Object.values(MEASURING_TYPES).map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell sx={{ borderBottom: "none", p: 0.4 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      className="custom-input"
                      value={row.quantity}
                      onChange={(e) => {
                        updateRow(idx, { quantity: e.target.value });
                        updateError(idx, { quantity: false });
                      }}
                      inputProps={{
                        min: 0,
                      }}
                      fullWidth
                      error={rowErr.quantity}
                    />
                  </TableCell>

                  <TableCell sx={{ borderBottom: "none", p: 0.4 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.manageStock}
                        onChange={(e) =>
                          updateRow(idx, { manageStock: e.target.value })
                        }
                        size="small"
                        className="custom-input"
                        sx={{
                          width: "100%",
                          "& .MuiSelect-select": { py: "4px" },
                        }}
                      >
                        <MenuItem
                          value={ManageStock.MANAGE_STOCK_AT_TEST_LEVEL}
                        >
                          {ManageStock.MANAGE_STOCK_AT_TEST_LEVEL}
                        </MenuItem>
                        <MenuItem
                          value={ManageStock.MANAGE_STOCK_AT_TEST_GROUP_LEVEL}
                        >
                          {ManageStock.MANAGE_STOCK_AT_TEST_GROUP_LEVEL}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell sx={{ borderBottom: "none", p: 0.4 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.deductStock}
                        onChange={(e) =>
                          updateRow(idx, { deductStock: e.target.value })
                        }
                        className="custom-input"
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiSelect-select": { py: "4.5px" },
                        }}
                      >
                        <MenuItem
                          value={DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION}
                        >
                          {DeductStock.DEDUCT_STOCK_AT_SAMPLE_COLLECTION}
                        </MenuItem>
                        <MenuItem
                          value={DeductStock.DEDUCT_STOCK_AT_PERFORM_TEST}
                        >
                          {DeductStock.DEDUCT_STOCK_AT_PERFORM_TEST}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ borderBottom: "none", p: 0.4 }}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => removeRow(idx)}
                    >
                      <Tooltip title="Close">
                        <span>
                          <CloseIcon fontSize="small" color="error" />
                        </span>
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 1 }}>
                  No items
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} /> : "Update Items/Tools"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
